import { AccountApi, UsersApi } from '@/api';
import { UpdateProfileUserRequest } from '@/api/identity/request/users.request';
import { getImage, resolverWrapper } from '@/composables';
import {
    AuthenticatorSetupDto,
    ChangePasswordCommand,
    EnableAuthenticatorCommand,
    ForgotPasswordCommand,
    ImpersonateUserCommand,
    LoginCommand,
    MfaStatusDto,
    RecoveryCodesDto,
    ResetPasswordCommand,
    SendTwoFactorCodeCommand,
    TokenDto,
    UserProfileDto,
    VerifyResetPasswordTokenCommand,
    VerifyTwoFactorCodeCommand,
} from '@/models';
import router from '@/routes/index';
import { HttpClient } from '@/services/HttpClient';
import { Guid } from '@/shared/guid';
import { TimerProcess, useMedicalServicesStore } from '@/store/medical.store';
import dayjs from 'dayjs';
import { defineStore } from 'pinia';

let refreshPromise: Promise<void> | null = null;
let refreshInProgress = false;

export const useIdentityStore = defineStore({
    id: 'identity',
    persist: true,
    state: () => ({
        data: null as TokenDto | null | undefined,
        returnUrl: null as any | null,
        selectedProvider: null as string | null,
        twoFactorRequestId: null as string | null,
        totpSetupRequired: false,
        api: new AccountApi(),
        userApi: new UsersApi(),
        mfaStatus: null as MfaStatusDto | null,
        authenticatorSetup: null as AuthenticatorSetupDto | null,
        recoveryCodes: null as RecoveryCodesDto | null,
    }),
    getters: {
        avatar: (state) =>
            state?.data?.profile?.avatar ? getImage(state?.data?.profile?.avatar) : undefined,

        refreshTokenExpired: (state) => () =>
            state.data == null || dayjs(state.data.refreshTokenExpiryTime).isBefore(dayjs()),

        isRefreshing: () => () => refreshInProgress,

        userFullName: (state) =>
            `${state.data?.profile?.name ?? 'unknown'} ${
                state.data?.profile?.surName ?? ' stranger'
            }`,

        currentUserEmail: (state) => state.data?.profile?.email ?? '',
    },
    actions: {
        sanitizeTokenData(token?: TokenDto | null) {
            if (!token) return token;
            return {
                ...token,
                token: '',
                refreshToken: '',
            } as TokenDto;
        },

        setReturnUrl(returnUrl) {
            this.returnUrl = returnUrl;
        },

        authorized(
            allOf: string[] | undefined,
            anyOf: string[] | undefined,
            notAnyOf: string[] | undefined = undefined
        ): boolean {
            let isAllOf = false;
            let isAnyOf = false;
            let isNotAnyOf = false;

            if (!allOf) isAllOf = true;
            else
                isAllOf = allOf.every((val) => {
                    const includes = this.data?.permissions?.includes(val);
                    if (!includes)
                        console.warn(
                            'FrontEnd Missing Permission: ' + val + ' From AllOf: ' + allOf
                        );
                    return includes;
                });

            if (!anyOf || anyOf.length == 0) isAnyOf = true;
            else {
                isAnyOf = anyOf.some((val) => this.data?.permissions?.includes(val));
                if (!isAnyOf) console.warn('FrontEnd Missing Permission From AnyOf: ' + anyOf);
            }
            if (!notAnyOf || notAnyOf.length == 0) isNotAnyOf = true;
            else {
                isNotAnyOf = !notAnyOf.some((val) => this.data?.permissions?.includes(val));
                if (!notAnyOf) console.warn('FrontEnd Missing Permission From AnyOf: ' + anyOf);
            }
            return isAllOf && isAnyOf && isNotAnyOf;
        },

        // Login
        async login(username, password) {
            return await this.api
                .accountLogin({ username, password } as LoginCommand)
                .then(async (response) => {
                    if (!response.data.success || !response.data.data) {
                        return Promise.reject(response.data.message);
                    }

                    if (response.data.data.totpSetupRequired) {
                        this.totpSetupRequired = true;
                        router.push('/2fa');
                        return;
                    }

                    if (response.data.data.twoFactorRequired) {
                        this.totpSetupRequired = false;
                        router.push('/2fa');
                        return;
                    }

                    this.data = this.sanitizeTokenData(response.data.data);
                    this.totpSetupRequired = false;
                    if (response.data.data.requirePasswordUpdate) {
                        router.push({ name: 'UpdatePassword' });
                        this.returnUrl = router.currentRoute;
                    } else {
                        router.push(this.returnUrl || '/');
                        this.returnUrl = null;
                    }

                    return;
                });
        },

        async impersonateAccount(userId) {
            return await this.api
                .accountImpersonateUser({ userId } as ImpersonateUserCommand)
                .then(async (response) => {
                    if (response.data.success && response.data.data) {
                        this.data = null;
                        if (
                            window.sessionStorage.getItem('medicalServices') ||
                            window.localStorage.getItem('medicalServices')
                        ) {
                            useMedicalServicesStore().resetTimer(TimerProcess.MASSAGE);
                            useMedicalServicesStore().resetTimer(TimerProcess.NONSURGICAL);
                        }
                        window.localStorage.removeItem('medicalServices');
                        window.localStorage.removeItem('humanResources');
                        this.data = this.sanitizeTokenData(response.data.data);
                        await router.push('/');
                        location.reload();
                        this.returnUrl = null;
                    } else {
                        return Promise.reject(response.data.message);
                    }
                });
        },

        async logout() {
            await HttpClient.post('Account/Logout', {}).catch(() => Promise.resolve());
            this.data = null;
            this.totpSetupRequired = false;
            if (
                window.sessionStorage.getItem('medicalServices') ||
                window.localStorage.getItem('medicalServices')
            ) {
                useMedicalServicesStore().resetTimer(TimerProcess.MASSAGE);
                useMedicalServicesStore().resetTimer(TimerProcess.NONSURGICAL);
            }
            window.localStorage.removeItem('medicalServices');
            window.localStorage.removeItem('humanResources');
            if (router.currentRoute.value.name != 'Login') router.push('/login');
        },

        async waitForRefresh(): Promise<boolean> {
            if (!refreshPromise) {
                return !this.refreshTokenExpired();
            }

            try {
                await refreshPromise;
            } catch {
                return false;
            }

            return !this.refreshTokenExpired();
        },

        async refresh() {
            if (refreshPromise) {
                return refreshPromise;
            }

            refreshInProgress = true;

            const request = this.api
                .accountRefresh({} as any)
                .then(async (response) => {
                    if (response.data.success) {
                        this.data = this.sanitizeTokenData(response.data.data);
                        return Promise.resolve();
                    }

                    return Promise.reject(response.data.message);
                })
                .catch((error) => {
                    this.data = undefined;
                    return Promise.reject(error);
                })
                .finally(() => {
                    refreshInProgress = false;
                    refreshPromise = null;
                });

            refreshPromise = request;
            return request;
        },

        // TwoFactor
        async getTwoFactorProviders(csignal): Promise<string[]> {
            this.selectedProvider = null;
            this.twoFactorRequestId = null;
            const userRequest = this.api.accountGetTwoFactorProviders({ signal: csignal });
            let providers: string[] = [];
            await resolverWrapper(
                userRequest,
                (response) => {
                    providers = response.data as string[];
                },
                {
                    reject: () => router.push('/login'),
                }
            );
            return providers;
        },
        resetTwoFactorRequestId() {
            this.twoFactorRequestId = null;
        },
        selectProvider(provider) {
            if (this.selectedProvider !== provider) {
                this.twoFactorRequestId = null;
            }
            this.selectedProvider = provider;
        },
        async sendTwoFactorCode() {
            if (!this.twoFactorRequestId) {
                this.twoFactorRequestId = String(Guid.create());
            }
            const response = await this.api.accountSendCode(
                {
                    provider: this.selectedProvider,
                } as SendTwoFactorCodeCommand,
                this.twoFactorRequestId
            );
            if (!response.data.success) {
                router.push('/login');
                return Promise.reject(response.data.message);
            }
        },

        async verifyTwoFactorCode(provider, code, rememberClient = false) {
            const response = await this.api.accountVerifyCode({
                provider,
                code,
                rememberClient,
            } as VerifyTwoFactorCodeCommand);
            if (response.data.success && response.data.data) {
                this.data = this.sanitizeTokenData(response.data.data);
                this.totpSetupRequired = false;
                router.push(this.returnUrl || '/');
                this.returnUrl = null;
                this.selectedProvider = null;
                this.twoFactorRequestId = null;
            } else {
                return Promise.reject(response.data.message);
            }
        },

        // Password
        async forgotPassword(email: string) {
            return await this.api.accountForgotPassword(
                { email } as ForgotPasswordCommand,
                String(Guid.create())
            );
        },

        async verifyResetPasswordToken(email: string, code: string) {
            return await this.api.accountVerifyResetPasswordToken(
                {
                    email,
                    code,
                } as VerifyResetPasswordTokenCommand,
                String(Guid.create())
            );
        },

        async resetPassword(
            email: string,
            code: string,
            password: string,
            passwordConfirm: string,
            xRequestId?: string
        ) {
            const response = await this.api.accountResetPassword(
                {
                    email,
                    code,
                    password,
                    passwordConfirm,
                } as ResetPasswordCommand,
                xRequestId
            );
            if (!response.data.success) {
                return Promise.reject(response.data.message);
            } else {
                router.push('/login');
                return response;
            }
        },

        async changePassword(
            currentPassword: string,
            newPassword: string,
            confirmPassword: string,
            xRequestid?: string
        ) {
            return await this.api.accountChangePassword(
                {
                    currentPassword,
                    newPassword,
                    confirmPassword,
                } as ChangePasswordCommand,
                xRequestid
            );
        },

        async updateUserProfile(request: UpdateProfileUserRequest) {
            return this.api.accountUpdateProfile(
                request.name,
                request.surName,
                request.phoneNumber,
                request.birthDay.toString(),
                request.avatar,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
        },

        setProfile(request: UserProfileDto) {
            const newData = {} as TokenDto;
            Object.entries(this.data as object).forEach((e) => {
                const [key, val] = e;
                if (key === 'profile') {
                    newData[key] = request;
                } else {
                    newData[key] = val;
                }
            });
            this.data = this.sanitizeTokenData(newData);
        },

        async updateCulture(userId: string, cultureCode: string, cultureShortCode) {
            (this.data as TokenDto).cultureCode = cultureCode;
            (this.data as TokenDto).cultureShortCode = cultureShortCode;
            return await this.api.accountUpdateCulture(
                { userId, cultureCode, cultureShortCode },
                String(Guid.create())
            );
        },

        // MFA
        async loadMfaStatus() {
            const response = await this.api.accountGetMfaStatus();
            if (response.data.success) {
                this.mfaStatus = response.data.data as MfaStatusDto;
            }
            return response;
        },

        async loadAuthenticatorSetup() {
            const response = await this.api.accountGetAuthenticatorSetup();
            if (response.data.success) {
                this.authenticatorSetup = response.data.data as AuthenticatorSetupDto;
            }
            return response;
        },

        async enableAuthenticator(code: string, requireTotp = false) {
            const response = await this.api.accountEnableAuthenticator(
                { code, requireTotp } as EnableAuthenticatorCommand,
                String(Guid.create())
            );
            if (response.data.success) {
                this.recoveryCodes = response.data.data as RecoveryCodesDto;
            }
            return response;
        },

        async disableAuthenticator() {
            const response = await this.api.accountDisableAuthenticator({}, String(Guid.create()));
            if (response.data.success) {
                this.recoveryCodes = null;
                this.authenticatorSetup = null;
            }
            return response;
        },

        async disableAuthenticatorForUser(userId: string) {
            return await HttpClient.put(
                `Users/${userId}/DisableAuthenticator`,
                { userId },
                {
                    headers: {
                        'x-requestid': String(Guid.create()),
                    },
                }
            );
        },

        async resetRecoveryCodes() {
            const response = await this.api.accountResetRecoveryCodes({}, String(Guid.create()));
            if (response.data.success) {
                this.recoveryCodes = response.data.data as RecoveryCodesDto;
            }
            return response;
        },
    },
});
